import { useMemo } from 'react';

import type { MultiSelectOption } from '@rtkwlf/fenrir-react';
import type { GetFlowDataRequest } from '@rtkwlf/rendall';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import type { GridSortModel } from '@packages/fenrir/components';
import { DataGridProSortParam } from '@packages/utils/use-query-params/data-grid-pro-sort-param/data-grid-pro-sort-param';
import { MultiSelectOptionParam } from '@packages/utils/use-query-params/multi-select-option-param/multi-select-option-param';

import { useFlowDataFilterParams } from '../use-flow-data-filter-params/use-flow-data-filter-params';
import { useFlowDataTypeParam } from '../use-flow-data-type-param/use-flow-data-type-param';

const getOptionToString = (options: MultiSelectOption[] | undefined) =>
  options?.map((option) => option.value).join(',');

const formatSortString = (sortArray: GridSortModel) =>
  sortArray
    .map(({ field, sort }) =>
      field && sort ? `${sort === 'desc' ? '-' : ''}${field}` : '',
    )
    .join(',');

export const useFlowDataRequest = (): Omit<
  GetFlowDataRequest,
  'organizationId'
> => {
  const flowDataTypeParam = useFlowDataTypeParam();
  const { flowDataFilterParams } = useFlowDataFilterParams();

  const {
    dateRange: [startDate, endDate],
  } = flowDataFilterParams;

  const [queryParams] = useQueryParams({
    device: MultiSelectOptionParam,
    protocol: MultiSelectOptionParam,
    location: MultiSelectOptionParam,
    site: MultiSelectOptionParam,
    sensor: MultiSelectOptionParam,
    direction: withDefault(StringParam, undefined),
    sort: DataGridProSortParam,
  });

  return useMemo(
    () => ({
      type: flowDataTypeParam,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      location: getOptionToString(queryParams.location),
      site: getOptionToString(queryParams.site),
      sensor: getOptionToString(queryParams.sensor),
      protocol: getOptionToString(queryParams.protocol),
      device: getOptionToString(queryParams.device),
      direction: queryParams.direction,
      [flowDataTypeParam]: getOptionToString(queryParams[flowDataTypeParam]),
      sort: formatSortString(queryParams.sort ?? []),
    }),
    [queryParams, startDate, endDate, flowDataTypeParam],
  );
};
